/**
 * Invoice create/edit — one component for both `new` and `:id/edit`, switched
 * on whether `useParams().id` is present. Field set matches
 * `invoiceCreateSchema` (refactor/backend/src/modules/payments/serializers.ts:205-214);
 * create uses `POST payments/invoices/`, edit uses `PATCH payments/invoices/{id}/`.
 *
 * This is the PLAIN invoice CRUD endpoint — it does NOT touch the ledger (no
 * DEBIT is created) and does NOT settle a balance. That only happens via
 * "Record payment" on the invoice detail page. The note below is here so a
 * manager creating/editing an invoice does not mistake this for collecting
 * rent.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { createInvoice, getInvoice, updateInvoice } from '../../api/endpoints/payments';
import { CHARGE_TYPES, INVOICE_STATUSES, type ChargeType, type InvoiceCreateRequest, type InvoiceStatus } from '../../types/payment';

const INITIAL_FORM = {
  lease: '',
  unit: '',
  charge_type: 'RENT' as ChargeType,
  amount_due: '',
  due_date: '',
  billing_period: '',
  status: 'OPEN' as InvoiceStatus,
  notes: '',
};

type FormState = typeof INITIAL_FORM;

function toPayload(form: FormState): InvoiceCreateRequest {
  return {
    lease: Number(form.lease),
    unit: Number(form.unit),
    charge_type: form.charge_type,
    amount_due: form.amount_due,
    due_date: form.due_date,
    billing_period: form.billing_period,
    status: form.status,
    notes: form.notes,
  };
}

export function InvoiceFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const invoiceId = Number(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const existing = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    enabled: isEdit && Number.isFinite(invoiceId),
  });

  useEffect(() => {
    if (existing.data === undefined) return;
    const invoice = existing.data;
    setForm({
      lease: String(invoice.lease),
      unit: String(invoice.unit),
      charge_type: invoice.charge_type,
      amount_due: invoice.amount_due,
      due_date: invoice.due_date,
      billing_period: invoice.billing_period,
      status: invoice.status,
      notes: invoice.notes,
    });
  }, [existing.data]);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  const save = useMutation({
    mutationFn: () =>
      isEdit ? updateInvoice(invoiceId, toPayload(form)) : createInvoice(toPayload(form)),
    onSuccess: (invoice) => navigate(isEdit ? '..' : `../${invoice.id}`, { replace: true }),
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    save.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  if (isEdit && existing.isPending) return <p>Loading…</p>;
  if (isEdit && existing.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(existing.error).message}
      </p>
    );
  }

  return (
    <section>
      <h2>{isEdit ? 'Edit invoice' : 'Add invoice'}</h2>

      <p role="note">
        This creates or updates the invoice record only — it does not record a payment or write a
        ledger entry. Use "Record payment" on the invoice detail page to settle a balance.
      </p>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="invoice-lease">Lease id</label>
        <input
          id="invoice-lease"
          type="number"
          required
          value={form.lease}
          onChange={(event) => update('lease', event.target.value)}
        />
        {fieldError('lease') !== undefined && (
          <p className="form__field-error">{fieldError('lease')}</p>
        )}

        <label htmlFor="invoice-unit">Unit id</label>
        <input
          id="invoice-unit"
          type="number"
          required
          value={form.unit}
          onChange={(event) => update('unit', event.target.value)}
        />
        {fieldError('unit') !== undefined && <p className="form__field-error">{fieldError('unit')}</p>}

        <label htmlFor="invoice-charge-type">Charge type</label>
        <select
          id="invoice-charge-type"
          value={form.charge_type}
          onChange={(event) => update('charge_type', event.target.value)}
        >
          {CHARGE_TYPES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="invoice-amount-due">Amount due</label>
        <input
          id="invoice-amount-due"
          required
          value={form.amount_due}
          onChange={(event) => update('amount_due', event.target.value)}
        />

        <label htmlFor="invoice-due-date">Due date</label>
        <input
          id="invoice-due-date"
          type="date"
          required
          value={form.due_date}
          onChange={(event) => update('due_date', event.target.value)}
        />

        <label htmlFor="invoice-billing-period">Billing period</label>
        <input
          id="invoice-billing-period"
          type="date"
          required
          value={form.billing_period}
          onChange={(event) => update('billing_period', event.target.value)}
        />

        <label htmlFor="invoice-status">Status</label>
        <select
          id="invoice-status"
          value={form.status}
          onChange={(event) => update('status', event.target.value)}
        >
          {INVOICE_STATUSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <label htmlFor="invoice-notes">Notes</label>
        <textarea
          id="invoice-notes"
          value={form.notes}
          onChange={(event) => update('notes', event.target.value)}
        />

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>
    </section>
  );
}

export default InvoiceFormPage;
